import React, { useMemo } from 'react';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';

const TRIGGER_LABELS = {
  upload_checkbox: 'Marked at upload',
  manual_mark: 'Manually marked',
  added_to_chron: 'Added to Medical Chronology',
  demotion: 'Removed from medical records',
};

/**
 * @param {string|null} triggerSource
 * @param {string|null} reason
 * @returns {string}
 */
export const mapPromotionTriggerText = (triggerSource, reason) => {
  if (triggerSource === 'demotion') {
    return reason || TRIGGER_LABELS.demotion;
  }

  return TRIGGER_LABELS[triggerSource] || triggerSource || '—';
};

/**
 * Collapsible audit log for HealthLake promotion/demotion events.
 * @param {{ promotionLog: Array<{ action: string, timestamp: string, user: string, triggerSource: string|null, reason: string|null }> }} props
 */
const PromotionLogPanel = ({ promotionLog = [] }) => {
  const sortedLog = useMemo(
    () => [...promotionLog].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)),
    [promotionLog]
  );

  return (
    <div data-testid="promotion-log-panel">
      <Panel header="Medical record history" toggleable collapsed>
        {sortedLog.length === 0 ? (
          <p className="text-muted mb-0" data-testid="promotion-log-empty">
            No medical record history
          </p>
        ) : (
          <ul className="list-unstyled mb-0" data-testid="promotion-log-list">
            {sortedLog.map((entry, index) => {
              const isPromoted = entry.action === 'promoted';
              const actionLabel = isPromoted ? 'Promoted' : 'Demoted';

              return (
                <li key={`${entry.timestamp}-${index}`} className="mb-2 pb-2 border-bottom" data-testid={`promotion-log-entry-${index}`}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Tag value={actionLabel} severity={isPromoted ? 'success' : 'warning'} data-testid={`promotion-log-action-${index}`} />
                    <span data-testid={`promotion-log-trigger-${index}`}>{mapPromotionTriggerText(entry.triggerSource, entry.reason)}</span>
                  </div>
                  <div className="text-muted F-size12 mt-1">
                    {entry.timestamp && <span data-testid={`promotion-log-timestamp-${index}`}>{entry.timestamp}</span>}
                    {entry.user && (
                      <span data-testid={`promotion-log-user-${index}`}>
                        {entry.timestamp ? ' · ' : ''}
                        {entry.user}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default PromotionLogPanel;
